import React, { useContext, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { User, OnboardingPreferences } from '../api';
import type { Environment } from '@mapp-byte-connect/config';

type AuthMethod = 'mapp' | 'email' | null;
type AuthStep =
  | 'idle'
  | 'email-input'
  | 'otp-verification'
  | 'authenticated'
  | 'onboarding'
  | 'mapp-denied';

interface UserContextValue {
  // Auth state
  isLoading: boolean;
  user?: User;
  authMethod: AuthMethod;
  authStep: AuthStep;
  isFirstLogin: boolean;
  isMAppContext: boolean;

  // Environment
  brandId: string | null;
  environment: Environment;
  setBrandContext: (brandId: string | null) => void;

  // MApp auth
  initMAppAuth: () => void;

  // Email/OTP auth
  requestOTP: (email: string) => Promise<{ success: boolean; error?: string }>;
  verifyOTP: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  pendingEmail: string | null;

  // Onboarding
  completeOnboarding: (preferences: OnboardingPreferences) => Promise<{ success: boolean; error?: string }>;
  skipOnboarding: () => void;

  // Internal onboarding tour
  showInternalOnboarding: boolean;
  completeInternalOnboarding: (preferences?: { timezone: string; emailNotifications: boolean }) => void;
  triggerOnboarding: () => void;
  dismissOnboarding: () => void;

  // Common
  logout: () => void;
  setAuthStep: (step: AuthStep) => void;
}

export const UserContext = React.createContext<UserContextValue>({
  isLoading: false,
  user: undefined,
  authMethod: null,
  authStep: 'idle',
  isFirstLogin: false,
  isMAppContext: false,
  brandId: null,
  environment: 'local',
  setBrandContext: () => {},
  initMAppAuth: () => {},
  requestOTP: async () => ({ success: false }),
  verifyOTP: async () => ({ success: false }),
  pendingEmail: null,
  completeOnboarding: async () => ({ success: false }),
  skipOnboarding: () => {},
  showInternalOnboarding: false,
  completeInternalOnboarding: () => {},
  triggerOnboarding: () => {},
  dismissOnboarding: () => {},
  logout: () => {},
  setAuthStep: () => {},
});

interface UserContextProviderProps {
  children: ReactNode;
}

export function UserContextProvider({ children }: UserContextProviderProps): React.JSX.Element {
  const auth = useAuth();

  return (
    <UserContext.Provider value={auth}>
      {children}
    </UserContext.Provider>
  );
}

export const useUserContext = () => useContext(UserContext);
